import { CodeFilter, getLibrary, LibraryListItem } from '@/shared/helpers/api';
import { Library } from '@/shared/helpers/api/types/entities';
import { http } from '@/shared/httpWrapper';
import { CreatePackageParameters, moveLibraryElement, ShippingWizard } from './wizard';
import { ExtractionSpecification } from './extractionSpecification';

// This is how content package extraction is sent to the server
export type ContentExtractionSpecification = {
    libraryName: string;
    codeFilter: CodeFilter;
    codeSetNames: string[];
};

// This is how the UI works with content extraction
export type ContentExtractionSpecificationInProgress = {
    libraryName: string;
    regEx: string;
    queries: string[][];
    columns: {
        name: string;
        include: boolean;
        required: boolean;
    }[];
    codeSets: {
        name: string;
        description: string;
        include: boolean;
    }[];
};

// Converts the UI format to the server format
function buildContentSpecification(inProgress: ContentExtractionSpecificationInProgress): ContentExtractionSpecification {
    return {
        libraryName: inProgress.libraryName,
        codeFilter: {
            regEx: [inProgress.regEx],
            queries: inProgress.queries,
            columns: inProgress.columns.filter(x => x.include).map(x => x.name)
        },
        codeSetNames: inProgress.codeSets.filter(x => x.include).map(x => x.name)
    };
}

// Prepare the UI format for a newly selected library
function contentExtractionSpecFromLibrary(library: Library): ContentExtractionSpecificationInProgress {
    const extractionSpecContent: ContentExtractionSpecificationInProgress = {
        libraryName: library.name,
        regEx: '',
        queries: [],
        columns: library.attributeDefinitions.map(x => ({
            name: x.name,
            include: true,
            required: x.includeInIdentity
        })),
        codeSets: library.codeSets.map(x => ({
            name: x.name,
            description: x.description,
            include: true
        }))
    };

    extractionSpecContent.columns.unshift({
        name: 'IsValid',
        include: true,
        required: false
    });

    extractionSpecContent.columns.unshift({
        name: 'Description',
        include: true,
        required: false
    });

    extractionSpecContent.columns.unshift({
        name: 'Name',
        include: true,
        required: library.nameInIdentity
    });

    return extractionSpecContent;
}

async function loadContentExtractionSpecification(libraryName: string): Promise<ContentExtractionSpecificationInProgress> {
    return contentExtractionSpecFromLibrary(await loadLibraryForContent(libraryName));
}

async function loadLibraryForContent(libraryName: string): Promise<Library> {
    try {
        return await getLibrary(http, libraryName, {
            includeCodeSets: true,
            includeAttributeDefintions: true
        });
    } catch {
        throw new Error('Not a library: ' + libraryName);
    }
}

/*
    Restores a saved content extraction on top of the library's current definition.
    Columns, sets and filters that no longer exist are dropped and reported.
    Columns and sets added after the package was created start unchecked, unless the saved
    setup included all columns.
*/
export function contentSpecFromSaved(
    library: Library,
    saved: ContentExtractionSpecification): { specification: ContentExtractionSpecificationInProgress; dropped: string[] } {
    const specification = contentExtractionSpecFromLibrary(library);
    const dropped: string[] = [];
    const prefix = `${library.name}:`;

    const savedColumns = saved.codeFilter?.columns ?? [];
    const columnNames = new Set(specification.columns.map(x => x.name));
    // The server treats an empty column list as all columns, so restore it with every column checked
    const includeAllColumns = savedColumns.length === 0;
    specification.columns.forEach(column => column.include = includeAllColumns || column.required || savedColumns.includes(column.name));
    savedColumns
        .filter(name => !columnNames.has(name))
        .forEach(name => dropped.push(`${prefix} column ${name} no longer exists`));

    const savedCodeSets = saved.codeSetNames ?? [];
    const codeSetNames = new Set(specification.codeSets.map(x => x.name));
    // A single set can't be unchecked in the wizard, so it is always included
    specification.codeSets.forEach(codeSet => codeSet.include = specification.codeSets.length === 1 || savedCodeSets.includes(codeSet.name));
    savedCodeSets
        .filter(name => !codeSetNames.has(name))
        .forEach(name => dropped.push(`${prefix} set ${name} no longer exists`));

    specification.regEx = saved.codeFilter?.regEx?.[0] ?? '';

    for (const query of saved.codeFilter?.queries ?? []) {
        if (columnNames.has(query[0])) {
            specification.queries.push(query);
        } else {
            dropped.push(`${prefix} filter "${query.join(' ')}" uses column ${query[0]} which no longer exists`);
        }
    }

    return { specification, dropped };
}

export function newLibraryContentWizard(elementList: ExtractionSpecification[]): ShippingWizard<ContentExtractionSpecificationInProgress> {
    const elements = elementList as ContentExtractionSpecificationInProgress[];

    async function addLibrary(library: LibraryListItem): Promise<void> {
        elements.push(await loadContentExtractionSpecification(library.name));
    }

    function removeLibrary(name: string): void {
        const index = elements.findIndex(x => x.libraryName === name);
        elements.splice(index, 1);
    }

    async function loadSaved(options: object, availableLibraries: LibraryListItem[]): Promise<string[]> {
        const saved = (options as { extractionItems?: ContentExtractionSpecification[] }).extractionItems ?? [];
        const availableNames = new Set(availableLibraries.map(x => x.name));
        const dropped: string[] = [];

        for (const item of saved) {
            if (!availableNames.has(item.libraryName)) {
                dropped.push(`Library ${item.libraryName} no longer exists`);
                continue;
            }
            const result = contentSpecFromSaved(await loadLibraryForContent(item.libraryName), item);
            elements.push(result.specification);
            dropped.push(...result.dropped);
        }

        return dropped;
    }

    function finish(): CreatePackageParameters {
        const options = {
            extractionItems: elements.map(buildContentSpecification)
        };

        const description = `Library content: ${options.extractionItems.map(x => x.libraryName).join(', ')}.`;

        return {
            packageType: 'LibraryContentPackage',
            description,
            options
        };
    }

    return {
        type: 'LibraryContent',
        addLibrary,
        removeLibrary,
        moveLibrary: (name: string, offset: number) => moveLibraryElement(elements, name, offset),
        loadSaved,
        finish,
        elements
    };
}

export function includeAllSets(extraction: ContentExtractionSpecificationInProgress): void {
    extraction.codeSets.forEach(x => x.include = true);
}

export function unincludeAllSets(extraction: ContentExtractionSpecificationInProgress): void {
    extraction.codeSets.forEach(x => x.include = false);
}

export function addFilter(extraction: ContentExtractionSpecificationInProgress, column: string, operator: string, value: string): void {
    extraction.queries.push([column, operator, value]);
}

export function removeFilter(extraction: ContentExtractionSpecificationInProgress, index: number): void {
    extraction.queries.splice(index, 1);
}
